import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { Tags, Toggle } from 'components/Form/components/ComponentTypes';
import SorterColumn from '@src/components/Table/components/SorterColumn/SorterColumn';
import { TYPE_OF_COMPONENTS } from 'constants/commons';
import { get, set } from 'packages/utils/sessionStorage';
import IntlMessages from 'util/IntlMessages';

import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import { AUX_FIELDS_EXTRA, AUX_FIELDS_TABLE_FORM } from '../../../constants';
import { buildDataSource, buildTableParams } from '../../TableCard/constants';
import { useGenericTableData } from '../hooks/useGenericTableData';
import { widgetFilterValuesToColumnFilters } from '../utils/widgetFilterValuesToColumnFilters';

export const TABLE_SORT_STORAGE_KEY = 'metricsTable';

export const TABLE_SORT_FIELD_MAP = {
  eventDate: 'createdAtDevice._seconds',
};

export const SEARCHABLE_COLUMNS = [AUX_FIELDS_EXTRA.userName, AUX_FIELDS_EXTRA.eventType];

const auxColProps = { width: 'auto' };

const getComponentColumnAlign = componentType => {
  if (componentType === TYPE_OF_COMPONENTS.NUMBER) {
    return 'right';
  }
  if (componentType === TYPE_OF_COMPONENTS.TOGGLE) {
    return 'center';
  }
  return 'left';
};

const buildCustomKey = (componentId, label) => `${componentId}_${label?.replaceAll(' ', '_')}`;

const toBackendSortFields = sorterFields =>
  Object.entries(sorterFields)
    .filter(([, v]) => v?.order)
    .map(([col, { order }]) => ({
      name: TABLE_SORT_FIELD_MAP[col] ?? col,
      order,
    }));

const toDefaultSorterValues = (sortFields = []) =>
  sortFields.map(({ name, order }) => ({
    name: Object.keys(TABLE_SORT_FIELD_MAP).find(k => TABLE_SORT_FIELD_MAP[k] === name) ?? name,
    order,
  }));

export const getTableTotalEvents = data => data?.hits?.total?.value || 0;

const COMPONENT_TYPE_RENDERERS = (component, colDataIndex) => (_, value) => {
  if (!value) return null;
  switch (component.type) {
    case TYPE_OF_COMPONENTS.TAG:
      return value[colDataIndex] ? <Tags tags={value[colDataIndex]} /> : null;
    case TYPE_OF_COMPONENTS.TOGGLE:
      return <Toggle value={value[colDataIndex]} />;
    default:
      return (
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>{value[colDataIndex]}</span>
      );
  }
};

const COLUMN_RENDERERS = ({ key, orgId }) => {
  switch (key) {
    default:
      return undefined;
  }
};

export const buildTableDashboardColumns = (
  rawData,
  { queryParams, orgId, sortFields = [], onSortFieldsChange, widgetId },
) => {
  if (!rawData) return [];

  const columns = queryParams?.columns || [];
  const hits = rawData?.hits?.hits || [];
  const activeColumns = columns.filter(col => col?.isActive);

  return activeColumns.reduce((acc, column) => {
    const { componentId } = column;

    // Form component columns (dynamic fields from event type)
    const component = hits
      // eslint-disable-next-line no-underscore-dangle
      .flatMap(hit => hit._source?.eventType?.components || [])
      .find(c => c.id === componentId);

    const colDataIndex = buildCustomKey(componentId, component?.label?.name);
    if (component) {
      acc.push({
        title: component.label.name,
        dataIndex: colDataIndex,
        key: component.id,
        align: getComponentColumnAlign(component.type),
        render: COMPONENT_TYPE_RENDERERS(component, colDataIndex),
        className: 'cancelDrag',
        ...auxColProps,
      });
    }

    // Aux field columns (userName, eventDate, eventType, division, location)
    const auxField = AUX_FIELDS_TABLE_FORM.find(aux => aux.componentId === componentId);
    if (auxField) {
      const isSortable = Object.keys(TABLE_SORT_FIELD_MAP).includes(auxField.componentId);
      const label = (
        <IntlMessages id={`dashboards.events.table.auxFields.${auxField.componentId}`} />
      );

      const columnTitle = isSortable ? (
        <div className="gx-flex-row gx-guarnic-gap-2 gx-align-items-center">
          {label}
          <SorterColumn
            column={auxField.componentId}
            onChange={newSortFields => {
              const backendFormat = toBackendSortFields(newSortFields);
              const updated = {
                ...get(TABLE_SORT_STORAGE_KEY),
                [widgetId]: { sortFields: backendFormat },
              };
              set(TABLE_SORT_STORAGE_KEY, updated);
              onSortFieldsChange(updated);
            }}
            defaultValues={toDefaultSorterValues(sortFields)}
          />
        </div>
      ) : (
        label
      );

      acc.push({
        title: columnTitle,
        dataIndex: auxField.componentId,
        key: auxField.componentId,
        align: 'left',
        searchable: SEARCHABLE_COLUMNS.includes(auxField.componentId),
        render: COLUMN_RENDERERS({ key: auxField.componentId, orgId }),
        className: 'cancelDrag',
        ...auxColProps,
      });
    }

    return acc;
  }, []);
};

export const useTableData = props => {
  const {
    queryParams,
    eventTypes,
    elementsRef,
    widget,
    widgetFilterValues,
    widgetTableFilterHandlers,
  } = props;

  const userOrgDivisions = useSelector(({ divisions }) => divisions.assigned.data.divisions);

  const [sortFieldsByWidget, setSortFieldsByWidget] = useState(
    () => get(TABLE_SORT_STORAGE_KEY) ?? {},
  );

  const sortFieldsMemo = useMemo(
    () => sortFieldsByWidget[widget?.id]?.sortFields || [],
    [sortFieldsByWidget, widget?.id],
  );

  const tableCardForm = useMemo(
    () => eventTypes?.find(({ id }) => id === queryParams?.formId),
    [eventTypes, queryParams?.formId],
  );

  const boardDivision = useMemo(
    () => userOrgDivisions[tableCardForm?.divId || ''] || {},
    [userOrgDivisions, tableCardForm?.divId],
  );

  const columnSearchActionsRef = useRef();

  const columnFilters = useMemo(
    () => widgetFilterValuesToColumnFilters(widgetFilterValues),
    [widgetFilterValues],
  );

  const onTableChange = useCallback(
    (pagination, tableFilters) => {
      widgetTableFilterHandlers?.applyWidgetTableFiltersBatch?.(pagination, tableFilters);
    },
    [widgetTableFilterHandlers],
  );

  const buildParamsWithSort = useCallback(
    (qp, commonParams) =>
      buildTableParams({
        formId: qp?.formId,
        uid: commonParams.uid,
        orgId: commonParams.organizationId,
        range: commonParams.range,
        interval: commonParams.interval,
        divisionIds: qp?.divisionIds?.length > 0 ? qp.divisionIds : commonParams.divisionIds,
        triggerIds: qp?.triggerIds || [],
        lastPosition: commonParams.lastPosition,
        pageSize: commonParams.pageSize,
        locationIds: qp?.locationIds || [],
        locationGroupId: qp?.locationGroupId || null,
        sortFields: sortFieldsMemo,
        columnFilters,
      }),
    [sortFieldsMemo, columnFilters],
  );

  const buildColumnsWithSort = useCallback(
    (rawData, context) =>
      buildTableDashboardColumns(rawData, {
        ...context,
        sortFields: sortFieldsMemo,
        onSortFieldsChange: setSortFieldsByWidget,
        widgetId: widget?.id,
      }),
    [sortFieldsMemo, widget?.id],
  );

  const buildDataSourceWithDivision = useCallback(
    data => buildDataSource({ records: data, division: boardDivision }),
    [boardDivision],
  );

  const result = useGenericTableData({
    ...props,
    op: API_OPERATIONS.TABLE_DASHBOARD,
    buildParams: buildParamsWithSort,
    buildColumns: buildColumnsWithSort,
    buildDataSource: buildDataSourceWithDivision,
    getTotalEvents: getTableTotalEvents,
    additionalDependencies: [JSON.stringify(sortFieldsMemo)],
    columnFilters,
    columnSearchActionsRef,
    columnSearchHandlers: {
      // onColumnSearch: handleColumnSearch, //NOTE: we removed this for disabled column search
      onTableChange,
    },
  });

  useEffect(() => {
    if (!elementsRef?.current || !widget?.id) return;
    const { metrics, pageSize } = result.data || {};
    const rowsShown = Math.min(pageSize, metrics?.length ?? 0);
    const heightByTable = Math.min(7, Math.max(3, rowsShown * 0.35 + 1.5));
    elementsRef.current = {
      ...elementsRef.current,
      [widget.id]: heightByTable,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data?.metrics?.length, result.data?.pageSize, widget?.id]);

  return {
    ...result,
    data: result.data ? { ...result.data, tableCardForm } : null,
  };
};
